import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { fetchPaymentConfig, fetchPaymentDetails } from '../../services/paymentService';

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));
}

function formatCurrency(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) {
    return 'INR 0.00';
  }
  return `INR ${amount.toFixed(2)}`;
}

function formatField(value) {
  const text = String(value ?? '').trim();
  return text || 'Not provided';
}

function safeInvoiceNumber(payment) {
  if (payment?.invoiceNumber) {
    return payment.invoiceNumber;
  }

  const suffix = String(payment?.id || '')
    .replace(/[^a-fA-F0-9]/g, '')
    .slice(-8)
    .toUpperCase();

  return suffix ? `RACE-INV-${suffix}` : 'RACE-INV-00000000';
}

const PaymentSuccessPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [payment, setPayment] = useState(location.state?.payment || null);
  const [paymentLoading, setPaymentLoading] = useState(!location.state?.payment && !!searchParams.get('paymentId'));
  const [configLoading, setConfigLoading] = useState(true);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [error, setError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPdfMode, setIsPdfMode] = useState(false);
  const invoiceRef = useRef(null);

  const paymentId = searchParams.get('paymentId');

  useEffect(() => {
    let isMounted = true;

    const loadPayment = async () => {
      if (payment || !paymentId) {
        setPaymentLoading(false);
        return;
      }

      try {
        const response = await fetchPaymentDetails({ paymentId });
        if (!isMounted) {
          return;
        }

        setPayment(response.data || null);
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError.message || 'Unable to load payment details.');
        }
      } finally {
        if (isMounted) {
          setPaymentLoading(false);
        }
      }
    };

    loadPayment();

    return () => {
      isMounted = false;
    };
  }, [payment, paymentId]);

  useEffect(() => {
    let isMounted = true;

    const loadPaymentConfig = async () => {
      try {
        const response = await fetchPaymentConfig();
        if (!isMounted) {
          return;
        }
        setPaymentConfig(response.data || null);
      } catch (configError) {
        if (isMounted) {
          setError((previousError) => previousError || configError.message || 'Unable to load invoice settings.');
        }
      } finally {
        if (isMounted) {
          setConfigLoading(false);
        }
      }
    };

    loadPaymentConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  const amountLabel = useMemo(() => formatCurrency(payment?.amount), [payment?.amount]);
  const invoiceNumber = useMemo(() => safeInvoiceNumber(payment), [payment]);
  const invoiceDateLabel = useMemo(
    () => formatDateTime(payment?.invoiceIssuedAt || payment?.paymentCompletedAt || payment?.createdAt),
    [payment]
  );
  const companyName = paymentConfig?.invoice?.companyName || 'Race of Advance Computing Education Private Limited';
  const logoUrl = `${process.env.PUBLIC_URL || ''}/favicon.png`;
  const isLoading = paymentLoading || configLoading;
  const PDF_CAPTURE_WIDTH = 1440;

  const handleDownload = async () => {
    if (!invoiceRef.current || isDownloading) {
      return;
    }

      try {
        setIsDownloading(true);
        setIsPdfMode(true);

        await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));

      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

        const canvas = await html2canvas(invoiceRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: PDF_CAPTURE_WIDTH,
          windowHeight: Math.max(invoiceRef.current.scrollHeight, 1600)
        });

      const imageData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;
      const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
      const imageWidth = canvas.width * ratio;
      const imageHeight = canvas.height * ratio;
      const x = (pageWidth - imageWidth) / 2;
      const y = margin + Math.max(0, (maxHeight - imageHeight) / 2);

      pdf.addImage(imageData, 'PNG', x, y, imageWidth, imageHeight, undefined, 'FAST');
      pdf.save(`${invoiceNumber || 'invoice'}.pdf`);
    } catch (downloadError) {
      console.error('Unable to generate invoice PDF:', downloadError);
      setError('Unable to download the invoice right now. Please try again.');
    } finally {
      setIsPdfMode(false);
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(135deg,#f8f4fb,#fff8f3)] px-4">
        <div className="rounded-2xl bg-white px-6 py-5 shadow-xl">
          <p className="text-sm font-medium text-gray-700">Loading invoice details...</p>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(135deg,#f8f4fb,#fff8f3)] px-4">
        <div className="max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
          <h1 className="text-2xl font-bold text-gray-900">Payment record not found</h1>
          <p className="mt-3 text-sm text-gray-600">{error || 'We could not load the completed payment details.'}</p>
          <Link
            to="/payment"
            className="mt-6 inline-flex rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white"
          >
            Back to payment page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-page min-h-screen bg-[linear-gradient(135deg,#f8f4fb,#fff8f3)] px-3 py-4 sm:px-6 sm:py-8 lg:px-8">
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 6mm;
          }

          html, body {
            background: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .invoice-page {
            background: #fff !important;
            padding: 0 !important;
          }

          .invoice-shell {
            max-width: 100% !important;
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
        }

        .pdf-mode .no-pdf {
          display: none !important;
        }

        .pdf-mode .compact-pad {
          padding-top: 8px !important;
          padding-bottom: 8px !important;
        }

        .pdf-mode .compact-gap {
          gap: 10px !important;
        }

        .pdf-mode .compact-text {
          font-size: 11px !important;
          line-height: 1.35 !important;
        }

        .pdf-mode .compact-title {
          font-size: 18px !important;
          line-height: 1.2 !important;
        }

        .pdf-mode .invoice-shell {
          width: 1123px !important;
          max-width: 1123px !important;
        }

        .pdf-mode .invoice-top {
          flex-direction: row !important;
          align-items: flex-start !important;
          justify-content: space-between !important;
        }

        .pdf-mode .invoice-meta-grid {
          grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          min-width: 360px !important;
        }

        .pdf-mode .invoice-summary-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
        }

        .pdf-mode .invoice-actions {
          flex-direction: row !important;
          flex-wrap: wrap !important;
        }

        .pdf-mode .invoice-actions > * {
          width: auto !important;
          flex: 1 1 0% !important;
        }
      `}</style>

      <div
        ref={invoiceRef}
        className={`invoice-shell mx-auto max-w-5xl overflow-hidden rounded-[1.5rem] border border-white/70 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)] sm:rounded-[2rem] ${
          isPdfMode ? 'pdf-mode' : ''
        }`}
      >
        <div className="no-pdf bg-[linear-gradient(135deg,#0f766e,#16a34a_55%,#f59e0b)] px-4 py-6 text-white sm:px-6 sm:py-8">
          <div className="inline-flex rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] sm:px-4 sm:text-xs sm:tracking-[0.24em]">
            Payment Successful
          </div>
          <h1 className="mt-3 text-2xl font-bold sm:mt-4 sm:text-3xl">Thank you for your payment</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-emerald-50">
            Your payment has been confirmed and your invoice is ready below.
          </p>
        </div>

        <div className="grid gap-4 bg-[linear-gradient(180deg,#ffffff,#fafafa)] p-3 sm:gap-6 sm:p-4 sm:p-6">
          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
            <div className="compact-pad border-b border-dashed border-gray-200 px-4 py-4 sm:px-6 sm:py-5">
              <div className="invoice-top flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-sm sm:h-24 sm:w-24">
                    <img
                      src={logoUrl}
                      alt={`${companyName} logo`}
                      className="h-full w-full object-contain p-1.5 sm:p-2"
                      onError={(event) => {
                        event.currentTarget.src = `${process.env.PUBLIC_URL || ''}/favicon.png`;
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-500 sm:text-[11px] sm:tracking-[0.25em]">
                      Tax Invoice
                    </p>
                    <h2 className="compact-title mt-1 break-words text-lg font-bold text-gray-900 sm:text-2xl">
                      {companyName}
                    </h2>
                    <p className="mt-1 max-w-2xl text-xs leading-5 text-gray-600 sm:text-sm sm:leading-6">
                      A clean invoice generated from your successful payment.
                    </p>
                  </div>
                </div>

                <div className="invoice-meta-grid grid gap-3 sm:grid-cols-3">
                  <MetaPill label="Invoice No" value={invoiceNumber} />
                  <MetaPill label="Invoice Date" value={invoiceDateLabel} />
                  <MetaPill label="Status" value={(payment.status || 'success').toUpperCase()} />
                </div>
              </div>
            </div>

            <div className="compact-pad grid gap-3 px-4 py-4 sm:gap-4 sm:px-6 lg:grid-cols-3">
              <ModernInfoCard
                title="Receiver Option"
                value={formatField(payment.merchantAccountLabel || payment.merchantAccountId)}
                note="Payment went to the selected receiver option shown above."
              />
              <ModernInfoCard
                title="Customer"
                value={formatField(payment.customerName)}
                note={`${formatField(payment.email)} | ${formatField(payment.phone)}`}
              />
              <ModernInfoCard title="Amount Paid" value={amountLabel} note={formatField(payment.purpose)} />
            </div>

            <div className="compact-pad grid gap-3 border-t border-dashed border-gray-200 px-4 py-4 sm:gap-4 sm:px-6 lg:grid-cols-2">
              <InvoicePanel title="Payment Details">
                <InfoRow label="Razorpay Order ID" value={formatField(payment.razorpayOrderId)} />
                <InfoRow label="Razorpay Payment ID" value={formatField(payment.razorpayPaymentId)} />
                <InfoRow label="Paid At" value={formatDateTime(payment.paymentCompletedAt || payment.createdAt)} />
                <InfoRow
                  label="Merchant Account"
                  value={formatField(payment.merchantAccountLabel || payment.merchantAccountId)}
                />
              </InvoicePanel>

              <InvoicePanel title="Customer Details">
                <InfoRow label="Customer Name" value={formatField(payment.customerName)} />
                <InfoRow label="Email" value={formatField(payment.email)} />
                <InfoRow label="Phone" value={formatField(payment.phone)} />
                <InfoRow label="Purpose" value={formatField(payment.purpose)} />
              </InvoicePanel>
            </div>

            <div className="compact-pad border-t border-dashed border-gray-200 px-4 py-4 sm:px-6">
              <div className="invoice-summary-grid grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <SummaryTile label="Amount Paid" value={amountLabel} />
                <SummaryTile label="Invoice Number" value={invoiceNumber} />
                <SummaryTile label="Payment Status" value={(payment.status || 'success').toUpperCase()} />
                <SummaryTile label="Date" value={invoiceDateLabel} />
              </div>

              <div className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
                We have stored this transaction securely and marked it as successful.
              </div>
            </div>

            <div className="no-pdf grid gap-4 border-t border-dashed border-gray-200 px-4 py-4 sm:px-6 xl:grid-cols-[1.3fr_0.7fr]">
              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200">
                <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">Receipt Note</h3>
                <p className="mt-3 text-sm leading-7 text-gray-700">
                  Use the download button to save this invoice as a PDF or keep a physical copy for your records.
                </p>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200">
                <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">Actions</h3>
                <div className="invoice-actions mt-3 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                  >
                    {isDownloading ? 'Downloading...' : 'Download Invoice'}
                  </button>
                  <Link
                    to="/payment"
                    className="inline-flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 sm:w-auto"
                  >
                    Make another payment
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-700 sm:w-auto"
                  >
                    Go to home
                  </Link>
                </div>
              </div>
            </div>

            <div className="no-pdf px-4 pb-5 sm:px-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 text-xs leading-6 text-gray-500">
                Generated on {invoiceDateLabel}. Invoice reference {invoiceNumber}.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function InvoicePanel({ title, children }) {
  return (
    <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200">
      <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">{title}</h3>
      <div className="mt-4 space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="grid gap-1 rounded-2xl bg-white px-4 py-3 ring-1 ring-gray-200 sm:grid-cols-[180px_1fr] sm:gap-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="break-words text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

function ModernInfoCard({ title, value, note }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-500">{title}</p>
      <p className="mt-3 break-words text-lg font-bold text-gray-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-gray-600">{note}</p>
    </div>
  );
}

function MetaPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-gray-500">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-2 break-words text-sm font-semibold text-gray-900">{value}</p>
    </div>
  );
}

export default PaymentSuccessPage;
